import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, UploadCloud } from 'lucide-react';
import Sidebar from './Sidebar';
import Breadcrumb from './Breadcrumb';
import CommandPalette from './CommandPalette';
import JobProcessingCoordinator, { JobProcessingTray } from '../jobs/JobProcessingCoordinator';
import { useUIStore } from '../../store';
import useJobProcessingStore from '../../store/jobProcessingStore';
import toast from 'react-hot-toast';

const JOB_IMPORT_ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
];
const JOB_IMPORT_ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];
const JOB_IMPORT_MAX_SIZE = 10 * 1024 * 1024;
const UPLOAD_IN_PROGRESS_MESSAGE = 'Upload in progress. If you leave now, the draft won\'t be created.';

const isJobsRoute = (pathname = '') => pathname === '/jobs' || pathname.startsWith('/jobs/');

const hasDraggedFiles = (event) => {
  const types = Array.from(event.dataTransfer?.types || []);
  if (types.includes('Files')) return true;
  return Array.from(event.dataTransfer?.items || []).some(item => item.kind === 'file');
};

const isLocalFileDrop = (event) => Boolean(
  event.target?.closest?.('[data-job-import-dropzone="local"], [data-prevent-global-job-drop="true"]')
);

const getFileExtension = (file) => (
  file?.name?.includes('.') ? `.${file.name.split('.').pop().toLowerCase()}` : ''
);

const isAcceptedJobImportFile = (file) => (
  JOB_IMPORT_ACCEPTED_TYPES.includes(file?.type) ||
  JOB_IMPORT_ACCEPTED_EXTENSIONS.includes(getFileExtension(file))
);

const DashboardLayout = () => {
  const {
    sidebarOpen, toggleSidebar, isMobile, setIsMobile, setSidebarOpen,
    commandPaletteOpen, closeCommandPalette, toggleCommandPalette
  } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();
  const previousIsMobile = useRef(null);

  const [globalDragActive, setGlobalDragActive] = useState(false);
  const startImport = useJobProcessingStore(state => state.startImport);
  const uploadingImportCount = useJobProcessingStore(state => state.uploadingImportCount);
  const dragCounter = useRef(0);
  const globalDropEnabled = isJobsRoute(location.pathname);

  useEffect(() => {
    if (uploadingImportCount <= 0) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = UPLOAD_IN_PROGRESS_MESSAGE;
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadingImportCount]);

  const shouldHandleGlobalFileDrag = useCallback((event) => (
    globalDropEnabled &&
    hasDraggedFiles(event) &&
    !isLocalFileDrop(event)
  ), [globalDropEnabled]);

  useEffect(() => {
    if (!globalDropEnabled) {
      dragCounter.current = 0;
      setGlobalDragActive(false);
    }
  }, [globalDropEnabled]);

  const handleDragEnter = useCallback((e) => {
    if (!shouldHandleGlobalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setGlobalDragActive(true);
  }, [shouldHandleGlobalFileDrag]);

  const handleDragLeave = useCallback((e) => {
    if (!globalDropEnabled || !hasDraggedFiles(e) || isLocalFileDrop(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setGlobalDragActive(false);
    }
  }, [globalDropEnabled]);

  const handleDragOver = useCallback((e) => {
    if (!shouldHandleGlobalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
  }, [shouldHandleGlobalFileDrag]);

  const handleDrop = useCallback(async (e) => {
    if (!shouldHandleGlobalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setGlobalDragActive(false);
    dragCounter.current = 0;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (uploadingImportCount > 0) {
      toast.error('Another job description upload is still starting. Wait for the draft to appear before dropping another file.');
      return;
    }

    if (!isAcceptedJobImportFile(file)) {
      toast.error('Upload a PDF, DOC, DOCX, or TXT file.');
      return;
    }

    if (file.size > JOB_IMPORT_MAX_SIZE) {
      toast.error('File size must be less than 10MB.');
      return;
    }

    toast.loading('Starting AI draft extraction...', { id: 'global-upload' });
    const result = await startImport(file, { notifyEmail: false });
    if (result.success) {
      toast.success('Draft created. AI is working in the background.', { id: 'global-upload', duration: 5000 });
      navigate(result.job?.id ? `/jobs/${result.job.id}` : '/jobs');
    } else {
      toast.error(result.error, { id: 'global-upload' });
    }
  }, [navigate, shouldHandleGlobalFileDrag, startImport, uploadingImportCount]);

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  useEffect(() => {
    const checkViewport = () => {
      const nextIsMobile = window.innerWidth < 1024;
      setIsMobile(nextIsMobile);

      if (previousIsMobile.current === null || previousIsMobile.current !== nextIsMobile) {
        setSidebarOpen(!nextIsMobile);
      }

      previousIsMobile.current = nextIsMobile;
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [setIsMobile, setSidebarOpen]);

  // Global Cmd/Ctrl+K shortcut
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleCommandPalette();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [toggleCommandPalette]);

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
      const mainEl = document.getElementById('main-content');
      if (mainEl) mainEl.scrollTop = 0;
    }

    if (isMobile && sidebarOpen) {
      toggleSidebar();
    }
  };

  return (
    <div className="relative flex h-screen w-full overflow-hidden bg-white text-slate-900 selection:bg-sky-500 selection:text-white">
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onNavigate={handleNavigate}
        isMobile={isMobile}
      />

      {isMobile && !sidebarOpen && (
        <button
          onClick={toggleSidebar}
          className="fixed left-5 top-5 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-white/60 bg-white/90 text-[#0b1f3a] shadow-[0_8px_32px_rgba(15,23,42,0.12)] backdrop-blur-xl ring-1 ring-black/5 transition-all duration-300 hover:scale-105 hover:bg-white active:scale-95"
          aria-label="Open navigation"
        >
          <Menu size={20} strokeWidth={2.5} />
        </button>
      )}

      <main
        id="main-content"
        className="custom-scrollbar flex h-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden scroll-smooth"
      >
        <Breadcrumb />
        <div className="flex-1">
          <Outlet />
        </div>
      </main>

      <CommandPalette open={commandPaletteOpen} onClose={closeCommandPalette} />
      <JobProcessingCoordinator />
      <JobProcessingTray />

      {globalDragActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1f3a]/80 backdrop-blur-sm transition-all duration-300">
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-sky-400 bg-white/5 p-12 text-center shadow-2xl backdrop-blur-md">
            <span className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-sky-500/20 text-sky-300 ring-4 ring-sky-400/30">
              <UploadCloud size={40} />
            </span>
            <h2 className="font-display text-4xl font-normal text-white">Drop anywhere to extract Job Details</h2>
            <p className="mt-4 max-w-lg text-lg text-sky-100/80">We'll instantly create a draft and the AI will analyze it in the background.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
